// ── services/api/innerCircle.api.ts ──────────────────────────────────────────
// All /inner-circle/* and /uploads/* calls (matrimony, jobs, services,
// on-behalf-of consents, presigned uploads).
// ────────────────────────────────────────────────────────────────────────────

import { api } from './client';

// ── Consents ──────────────────────────────────────────────────────────────────
export type ModuleType = 'MATRIMONY' | 'JOB_LISTING' | 'JOB_SEEKER' | 'SERVICE';

export interface PendingConsent {
  id: string;
  nodeId: string;
  moduleType: ModuleType;
  draftPayload: Record<string, unknown>;
  createdByUserId: string;
  createdByName?: string;
  expiresAt: string;
}

// ── Matrimony ─────────────────────────────────────────────────────────────────
export interface MatrimonyProfile {
  id: string;
  nodeId: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  age?: number | null;
  gender?: string | null;
  height?: string | null;
  gotra?: string | null;
  dietaryPrefs?: string | null;
  city?: string | null;
  profession?: string | null;
  education?: string | null;
  aboutText?: string | null;
  photoUrls: string[];
  poolOptIn: boolean;
  minRelationDistance: number;
}

export interface MatrimonyCandidate {
  profileId: string;
  age?: number | null;
  city?: string | null;
  profession?: string | null;
  education?: string | null;
  sameTree: boolean;
  relationshipDistance: number | null;
  revealLevel: 'ANONYMOUS' | 'MUTUAL_INTEREST' | 'CONTACT';
  displayName?: string;
  aboutText?: string | null;
  photoUrls?: string[];
  height?: string | null;
  gotra?: string | null;
  dietaryPrefs?: string | null;
  vouches: VouchSummary;
}

// ── Jobs & services ───────────────────────────────────────────────────────────
export interface JobListing {
  id: string; title: string; company?: string | null; location?: string | null;
  remote: boolean; description?: string | null; closesAt?: string | null; postedByName: string;
  vouches: VouchSummary;
}
export interface JobSeeker {
  id: string; displayName: string; headline?: string | null; yearsExperience?: number | null;
  openToRemote: boolean; seekingRoles: string[]; hasCv: boolean;
  vouches: VouchSummary;
}
export interface ServiceListing {
  id: string; businessName: string; category?: string | null; description?: string | null;
  locationText?: string | null; photoUrls: string[]; providedByName: string;
  vouches: VouchSummary;
}

// Shapes returned by the "mine" endpoints (raw DB rows — more fields than the browse projections).
export interface MyJobListing {
  id: string; title: string; company?: string | null; location?: string | null;
  remote: boolean; description?: string | null; status: 'OPEN' | 'FILLED' | 'CLOSED'; closesAt?: string | null;
}
export interface MyServiceListing {
  id: string; businessName: string; category?: string | null; description?: string | null;
  locationText?: string | null; contactPhone?: string | null; photoUrls: string[]; status: 'ACTIVE' | 'PAUSED';
}
export interface MyJobSeeker {
  id: string; headline?: string | null; yearsExperience?: number | null; cvFileKey?: string | null;
  openToRemote: boolean; seekingRoles: string[]; status: 'OPEN' | 'PAUSED';
}

export interface VouchSummary {
  count: number;
  voucherNames: string[];
}

// Single-record shapes for "My Saved" — a bookmarked item may no longer be
// live/browsable, so these include the record's current status rather than
// 404ing on anything but a truly deleted record.
export interface MatrimonyProfileView {
  profileId: string;
  age?: number | null; city?: string | null; profession?: string | null; education?: string | null;
  status: string; poolOptIn: boolean;
  revealLevel: 'ANONYMOUS' | 'MUTUAL_INTEREST' | 'CONTACT';
  displayName?: string; aboutText?: string | null; photoUrls?: string[];
  height?: string | null; gotra?: string | null; dietaryPrefs?: string | null;
  vouches: VouchSummary;
}
export interface JobListingView extends JobListing { status: string }
export interface JobSeekerView extends JobSeeker { status: string }
export interface ServiceListingView extends ServiceListing { status: string }
export interface Vouch {
  id: string;
  voucherName: string;
  note?: string | null;
  createdAt: string;
}

export interface MatrimonyProfileInput {
  age?: number | undefined; gender?: string | undefined; height?: string | undefined;
  gotra?: string | undefined; dietaryPrefs?: string | undefined; city?: string | undefined;
  profession?: string | undefined; education?: string | undefined; aboutText?: string | undefined;
  partnerPreferences?: Record<string, unknown> | undefined; minRelationDistance?: number | undefined;
  photoUrls?: string[] | undefined;
}
export interface JobListingInput {
  title: string; company?: string | undefined; location?: string | undefined;
  remote?: boolean | undefined; description?: string | undefined; branchScope?: number | null | undefined;
}
export interface JobSeekerInput {
  headline?: string | undefined; yearsExperience?: number | undefined;
  openToRemote?: boolean | undefined; seekingRoles?: string[] | undefined; branchScope?: number | null | undefined;
}
export interface ServiceListingInput {
  businessName: string; category?: string | undefined; description?: string | undefined;
  locationText?: string | undefined; contactPhone?: string | undefined; photoUrls?: string[] | undefined;
  branchScope?: number | null | undefined;
}

export const innerCircleApi = {
  // ── Consents ──
  createConsent: (nodeId: string, moduleType: ModuleType, draftPayload: Record<string, unknown>) =>
    api.post<{ id: string }>('/inner-circle/consents', { nodeId, moduleType, draftPayload }),
  listPendingConsents: () =>
    api.get<PendingConsent[]>('/inner-circle/consents/pending'),
  approveConsent: (id: string) =>
    api.post<{ consentId: string; subjectRecordId: string; moduleType: ModuleType }>(`/inner-circle/consents/${id}/approve`),
  declineConsent: (id: string) =>
    api.post<{ success: boolean }>(`/inner-circle/consents/${id}/decline`),

  // ── Matrimony ──
  getMyMatrimonyProfile: () => api.get<MatrimonyProfile | null>('/inner-circle/matrimony/me'),
  createMatrimonyProfile: (input: MatrimonyProfileInput) =>
    api.post<MatrimonyProfile>('/inner-circle/matrimony', input),
  updateMatrimonyProfile: (id: string, patch: MatrimonyProfileInput) =>
    api.patch<MatrimonyProfile>(`/inner-circle/matrimony/${id}`, patch),
  // treeId narrows the pool to one book's Inner Circle section; omitted, this
  // is the global cross-community browse (used by the bottom-nav Inner Circle).
  browseMatrimony: (filters?: { city?: string; profession?: string; treeId?: string }) => {
    const qs = filters ? `?${new URLSearchParams(filters as Record<string, string>).toString()}` : '';
    return api.get<MatrimonyCandidate[]>(`/inner-circle/matrimony${qs}`);
  },
  getMatrimonyProfileById: (id: string) => api.get<MatrimonyProfileView>(`/inner-circle/matrimony/${id}`),
  activateMatrimonyProfile: (id: string) => api.post<MatrimonyProfile>(`/inner-circle/matrimony/${id}/activate`),
  pauseMatrimonyProfile: (id: string) => api.post<MatrimonyProfile>(`/inner-circle/matrimony/${id}/pause`),
  optIn: (id: string) => api.post<MatrimonyProfile>(`/inner-circle/matrimony/${id}/opt-in`),
  optOut: (id: string) => api.post<MatrimonyProfile>(`/inner-circle/matrimony/${id}/opt-out`),
  expressInterest: (id: string) => api.post(`/inner-circle/matrimony/${id}/express-interest`),
  revealContact: (id: string) => api.post<{ level: string }>(`/inner-circle/matrimony/${id}/reveal-contact`),

  // ── Jobs ──
  getMyJobListings: () => api.get<MyJobListing[]>('/inner-circle/jobs/listings/mine'),
  createJobListing: (input: JobListingInput) =>
    api.post<JobListing>('/inner-circle/jobs/listings', input),
  browseJobListings: (filters?: { remote?: boolean; treeId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.remote !== undefined) params.set('remote', String(filters.remote));
    if (filters?.treeId) params.set('treeId', filters.treeId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return api.get<JobListing[]>(`/inner-circle/jobs/listings${qs}`);
  },
  getJobListingById: (id: string) => api.get<JobListingView>(`/inner-circle/jobs/listings/${id}`),
  closeJobListing: (id: string) => api.post(`/inner-circle/jobs/listings/${id}/close`),
  fillJobListing: (id: string) => api.post(`/inner-circle/jobs/listings/${id}/fill`),
  reopenJobListing: (id: string) => api.post(`/inner-circle/jobs/listings/${id}/reopen`),

  getMyJobSeekerProfile: () => api.get<MyJobSeeker | null>('/inner-circle/jobs/seekers/me'),
  createJobSeekerProfile: (input: JobSeekerInput) =>
    api.post<JobSeeker>('/inner-circle/jobs/seekers', input),
  browseJobSeekers: (filters?: { treeId?: string }) => {
    const qs = filters?.treeId ? `?treeId=${encodeURIComponent(filters.treeId)}` : '';
    return api.get<JobSeeker[]>(`/inner-circle/jobs/seekers${qs}`);
  },
  updateJobSeekerProfile: (id: string, patch: { cvFileKey?: string }) =>
    api.patch<MyJobSeeker>(`/inner-circle/jobs/seekers/${id}`, patch),
  getJobSeekerById: (id: string) => api.get<JobSeekerView>(`/inner-circle/jobs/seekers/${id}`),
  pauseJobSeekerProfile: (id: string) => api.post(`/inner-circle/jobs/seekers/${id}/pause`),
  resumeJobSeekerProfile: (id: string) => api.post(`/inner-circle/jobs/seekers/${id}/resume`),

  // ── Services ──
  getMyServiceListings: () => api.get<MyServiceListing[]>('/inner-circle/services/mine'),
  createServiceListing: (input: ServiceListingInput) =>
    api.post<ServiceListing>('/inner-circle/services', input),
  updateServiceListing: (id: string, patch: Partial<ServiceListingInput>) =>
    api.patch<MyServiceListing>(`/inner-circle/services/${id}`, patch),
  browseServiceListings: (filters?: { category?: string; treeId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.category) params.set('category', filters.category);
    if (filters?.treeId) params.set('treeId', filters.treeId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return api.get<ServiceListing[]>(`/inner-circle/services${qs}`);
  },
  getServiceListingById: (id: string) => api.get<ServiceListingView>(`/inner-circle/services/${id}`),
  pauseServiceListing: (id: string) => api.post(`/inner-circle/services/${id}/pause`),
  resumeServiceListing: (id: string) => api.post(`/inner-circle/services/${id}/resume`),

  // ── Bookmarks ──
  listBookmarks: (moduleType?: ModuleType) => {
    const qs = moduleType ? `?moduleType=${moduleType}` : '';
    return api.get<{ id: string; moduleType: ModuleType; recordId: string }[]>(`/inner-circle/bookmarks${qs}`);
  },
  addBookmark: (moduleType: ModuleType, recordId: string) =>
    api.post<{ id: string }>('/inner-circle/bookmarks', { moduleType, recordId }),
  removeBookmark: (moduleType: ModuleType, recordId: string) =>
    api.delete<{ success: boolean }>(`/inner-circle/bookmarks/${moduleType}/${recordId}`),

  // ── Vouches ──
  createVouch: (moduleType: ModuleType, recordId: string, note?: string) =>
    api.post<Vouch>('/inner-circle/vouches', { moduleType, recordId, note }),
  retractVouch: (moduleType: ModuleType, recordId: string) =>
    api.delete<{ success: boolean }>(`/inner-circle/vouches/${moduleType}/${recordId}`),
  listVouches: (moduleType: ModuleType, recordId: string) =>
    api.get<Vouch[]>(`/inner-circle/vouches/${moduleType}/${recordId}`),
  getMyVouch: (moduleType: ModuleType, recordId: string) =>
    api.get<{ id: string } | null>(`/inner-circle/vouches/${moduleType}/${recordId}/mine`),

  // ── Admin: matrimony policy floor (MAT-12) ──
  getMatrimonyPolicy: (treeId: string) =>
    api.get<{ treeId: string; minRelationDistanceFloor: number }>(`/inner-circle/matrimony/policy/${treeId}`),
  setMatrimonyPolicy: (treeId: string, minRelationDistanceFloor: number) =>
    api.patch<{ treeId: string; minRelationDistanceFloor: number }>(`/inner-circle/matrimony/policy/${treeId}`, { minRelationDistanceFloor }),

  // ── Admin: upload limits ──
  getUploadLimit: (treeId: string, moduleType: 'MATRIMONY' | 'JOB_SEEKER' | 'SERVICE', kind: 'PHOTO' | 'CV') =>
    api.get<{ treeId: string; moduleType: string; kind: string; maxCount: number }>(`/uploads/limits/${treeId}?moduleType=${moduleType}&kind=${kind}`),
  setUploadLimit: (treeId: string, moduleType: 'MATRIMONY' | 'JOB_SEEKER' | 'SERVICE', kind: 'PHOTO' | 'CV', maxCount: number) =>
    api.patch<{ treeId: string; moduleType: string; kind: string; maxCount: number }>(`/uploads/limits/${treeId}`, { moduleType, kind, maxCount }),

  // ── Uploads ──
  presignUpload: (input: { nodeId: string; moduleType: 'MATRIMONY' | 'JOB_SEEKER' | 'SERVICE'; kind: 'PHOTO' | 'CV'; contentType: string; fileSize: number }) =>
    api.post<{ uploadUrl: string; fileKey: string; expiresInSeconds: number }>('/uploads/presigned', input),
  confirmUpload: (input: { nodeId: string; moduleType: 'MATRIMONY' | 'JOB_SEEKER' | 'SERVICE'; kind: 'PHOTO' | 'CV'; fileKey: string; contentType: string; sizeBytes: number }) =>
    api.post<{ fileKey: string; publicUrl: string }>('/uploads/confirm', input),
};
